import { HubConnection } from "@microsoft/signalr";
import React from "react";
import { useQueryClient } from "react-query";
import { aisRouterListQueryKey } from "~/pages/ais-vessel-page/hooks/useAISRouters";
import { aisListQueryKey } from "~/pages/ais-vessel-page/hooks/useAISVessels";
import { useTrafficBuoyRouters } from "~/pages/traffic-buoys/hooks/useTrafficBuoyRouters";
import { useProcessingFileUpdate } from "./useProcessingFileUpdate";
import { useSignalR } from "./useSignalR";

type InSeaHubContextState = {
  connected: boolean;
  initialLoading: boolean;
  connection: HubConnection | null;
};

const contextDefaultValues: InSeaHubContextState = {
  connected: false,
  initialLoading: true,
  connection: null,
};

export const InSeaHubContext =
  React.createContext<InSeaHubContextState>(contextDefaultValues);

type Props = {
  children: JSX.Element;
};

const routerUpdateListener = "TrafficBuoyRouterUpdate";
const vesselTelemetryUpdate = "VesselTelemetryUpdate";

export const InSeaHubProvider = ({ children }: Props) => {
  const { connected, connection, initialLoading } = useSignalR();
  const { handleTrafficBuoyRouterUpdate } = useTrafficBuoyRouters();
  useProcessingFileUpdate({ connected, connection });
  const qc = useQueryClient();
  React.useEffect(() => {
    if (connected) {
      // Add listeners

      connection.on(routerUpdateListener, handleTrafficBuoyRouterUpdate);
      connection.on(vesselTelemetryUpdate, () => {
        void qc.invalidateQueries(aisListQueryKey);
        void qc.invalidateQueries(aisRouterListQueryKey);
      });
    }
    return () => {
      // Remove listeners
      connection.off(routerUpdateListener);

      connection.off(aisRouterListQueryKey);
    };
  }, [connected, connection, handleTrafficBuoyRouterUpdate, qc]);

  const value: InSeaHubContextState = React.useMemo(
    () => ({ connected, initialLoading, connection }),
    [connected, initialLoading, connection],
  );

  return (
    <InSeaHubContext.Provider value={value}>
      {children}
    </InSeaHubContext.Provider>
  );
};
